import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

import { Form, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import DurationPicker from "./DurationPicker";

// Form validation schema (values in minutes)
const formSchema = z.object({
  max_booking_len: z.number().min(1, "Must be at least 1 minute"),
  advance_booking_cap: z.number().min(1, "Must be at least 1 minute"),
  booking_buffer: z.number().min(0, "Must be 0 or more"),
});

type FormValues = z.infer<typeof formSchema>;

const BookingSettings: React.FC = () => {
  const queryClient = useQueryClient();

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["booking-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("*")
        .in("name", ["max_booking_len", "advance_booking_cap", "booking_buffer"]);
      if (error) throw error;
      return data;
    },
  });

  // Update settings mutation
  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: async (values: FormValues) => {
      // Update each setting individually
      for (const [name, value] of Object.entries(values)) {
        console.log("Updating", name, "to", value);
        const { error } = await supabase
          .from("admin_settings")
          .update({ value: value.toString() })
          .eq("name", name);
        if (error) {
          console.error("Supabase update error:", error);
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-settings"] });
      toast.success("Settings updated successfully");
    },
    onError: (error) => {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    },
  });

  // Initialize form with current values (convert to numbers)
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      max_booking_len: Number(settings?.find(s => s.name === "max_booking_len")?.value || 60),
      advance_booking_cap: Number(settings?.find(s => s.name === "advance_booking_cap")?.value || 1440),
      booking_buffer: Number(settings?.find(s => s.name === "booking_buffer")?.value || 15),
    },
  });

  // Update form values when settings are loaded
  React.useEffect(() => {
    if (settings) {
      form.reset({
        max_booking_len: Number(settings.find(s => s.name === "max_booking_len")?.value || 60),
        advance_booking_cap: Number(settings.find(s => s.name === "advance_booking_cap")?.value || 1440),
        booking_buffer: Number(settings.find(s => s.name === "booking_buffer")?.value || 15),
      });
    }
  }, [settings, form]);

  const onSubmit = (values: FormValues) => {
    updateSettings(values);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center my-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Booking Settings</CardTitle>
        <CardDescription>
          Configure booking time limits and restrictions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6">
              <FormField
                control={form.control}
                name="max_booking_len"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Booking Length</FormLabel>
                    <Controller
                      control={form.control}
                      name="max_booking_len"
                      render={({ field: ctrlField }) => (
                        <DurationPicker
                          value={ctrlField.value}
                          onChange={ctrlField.onChange}
                          label=""
                          disabled={isPending}
                        />
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="advance_booking_cap"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time Limit for Booking in Advance</FormLabel>
                    <Controller
                      control={form.control}
                      name="advance_booking_cap"
                      render={({ field: ctrlField }) => (
                        <DurationPicker
                          value={ctrlField.value}
                          onChange={ctrlField.onChange}
                          label=""
                          disabled={isPending}
                        />
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="booking_buffer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Booking Buffer</FormLabel>
                    <Controller
                      control={form.control}
                      name="booking_buffer"
                      render={({ field: ctrlField }) => (
                        <DurationPicker
                          value={ctrlField.value}
                          onChange={ctrlField.onChange}
                          label=""
                          disabled={isPending}
                          units={["minutes"]}
                        />
                      )}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default BookingSettings; 